import { useMemo, useState } from 'react'
import { Info } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import modalStyles from '@/components/ui/claimsModals.module.css'
import { residentConfirmPresentation } from '../../constants/residentDialogProps'
import { updateResidentsStatus } from '../../services/residentApi'
import { getResidentMultiEditStatusMapping } from '../../utils/residentListConstants'
import type { RentalStatusDialogProps } from '../../types'
import {
  ResidentMultiEditInfoModal,
  type ResidentMultiEditInfoModalType,
} from './ResidentMultiEditInfoModal'

export function RentalStatusDialog({
  isOpen,
  selectedApplicantIds,
  onClose,
  onUpdated,
  statusMappings,
}: RentalStatusDialogProps) {
  const [residentStatus, setResidentStatus] = useState('')
  const [residentProgress, setResidentProgress] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [infoModalState, setInfoModalState] = useState<{
    isOpen: boolean
    type: ResidentMultiEditInfoModalType
  }>({
    isOpen: false,
    type: 'resident_status',
  })

  const residentStatusTree = useMemo(
    () => getResidentMultiEditStatusMapping(statusMappings),
    [statusMappings],
  )

  const progressOptions = residentStatus
    ? residentStatusTree[residentStatus]?.progress || {}
    : {}

  const submit = async () => {
    if (!residentStatus || !residentProgress) return
    setProcessing(true)
    try {
      await updateResidentsStatus({
        rental_status: residentStatus,
        rental_progress: residentProgress,
        page: 'resident',
        applicant_ids: selectedApplicantIds,
      })
      showSuccess('Status Updated Successfully')
      onUpdated()
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <>
      <OnScreenModal
        isOpen={isOpen}
        title="Update Resident Status"
        onClose={onClose}
        maxWidth="max-w-2xl"
        bodyScrollOnly
        overlayDataTestId="rental-status-dialog-overlay"
        panelDataTestId="rental-status-dialog"
        closeButtonDataTestId="rental-status-dialog-close-button"
        bodyClassName="p-4 sm:p-5"
        footer={
          <div
            className={modalStyles.modalFooterActionGroup}
            data-testid="rental-status-dialog-footer"
          >
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!residentStatus || !residentProgress || processing}
              data-testid="rental-status-dialog-submit-button"
              className={modalStyles.modalActionPrimary}
            >
              Submit
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              data-testid="rental-status-dialog-cancel-button"
              className={modalStyles.modalActionSecondary}
            >
              Cancel
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-[#232946]">
            <span className="flex items-center gap-1.5">
              <span>Resident Status</span>
              <button
                type="button"
                onClick={() => setInfoModalState({ isOpen: true, type: 'resident_status' })}
                className="inline-flex items-center text-slate-400 hover:text-blue-600 focus:outline-none"
                title="Resident Status Instructions"
                aria-label="Resident Status Instructions"
                data-testid="rental-status-dialog-status-info-button"
              >
                <Info className="h-4 w-4" />
              </button>
            </span>
            <select
              className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
              value={residentStatus}
              onChange={(event) => {
                setResidentStatus(event.target.value)
                setResidentProgress('')
              }}
              required
              data-testid="rental-status-dialog-status-select"
            >
              <option value="">Resident Status</option>
              {Object.entries(residentStatusTree).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.name || key}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-[#232946]">
            <span className="flex items-center gap-1.5">
              <span>Resident Progress</span>
              <button
                type="button"
                onClick={() => setInfoModalState({ isOpen: true, type: 'resident_progress' })}
                className="inline-flex items-center text-slate-400 hover:text-blue-600 focus:outline-none"
                title="Resident Progress Instructions"
                aria-label="Resident Progress Instructions"
                data-testid="rental-status-dialog-progress-info-button"
              >
                <Info className="h-4 w-4" />
              </button>
            </span>
            <select
              className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
              value={residentProgress}
              onChange={(event) => setResidentProgress(event.target.value)}
              required
              data-testid="rental-status-dialog-progress-select"
            >
              <option value="">Select Resident Progress</option>
              {Object.entries(progressOptions).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>
      </OnScreenModal>
      <ResidentMultiEditInfoModal
        isOpen={infoModalState.isOpen}
        onClose={() => setInfoModalState((prev) => ({ ...prev, isOpen: false }))}
        type={infoModalState.type}
        residentStatusMapping={statusMappings?.resident_status_mapping}
        selectedStatus={residentStatus}
      />
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Update Resident Status"
        message="Please confirm that you really want to update the resident status and progress of the selected residents."
        primaryLabel="Update"
        secondaryLabel="Cancel"
        onPrimary={() => {
          setConfirmOpen(false)
          void submit()
        }}
        onSecondary={() => setConfirmOpen(false)}
        onDismiss={() => setConfirmOpen(false)}
        {...residentConfirmPresentation}
        overlayDataTestId="rental-status-confirm-overlay"
        primaryButtonDataTestId="rental-status-confirm-update-button"
        secondaryButtonDataTestId="rental-status-confirm-cancel-button"
        closeButtonDataTestId="rental-status-confirm-close-button"
      />
    </>
  )
}
